import React, { useState, useEffect, useCallback } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBBtn,
  MDBModalBody,
  MDBModalFooter,
  MDBInput,
  MDBSpinner,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBRow,
  MDBCol,
  MDBIcon
} from "mdb-react-ui-kit";
import { updateMemberPlan } from "../services/apiServices";
import { BasePlanName } from "../utils/BasePlanName";
import PlanChangePayment from "../components/PlanChangePayment";

const ChangePlanModal = ({ open, setOpen, member, currentTransaction, plans, addons, businessTax, onSuccess, changeType = "plan" }) => {
  const [selectedItem, setSelectedItem] = useState(null);



  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [calculation, setCalculation] = useState({
    newItemPrice: 0,
    alreadyPaid: 0,
    remainingAmount: 0,
    taxableAmount: 0,
    taxAmount: 0,
    finalPayable: 0,
    isRefund: false,
    refundAmount: 0
  });

  // console.log("plansplans",plans)
  // Helper function to check if tax is included in the plan
  const isTaxIncluded = (item) => {
    if (!item) return true;
    // taxcheck: true means tax is already included in price
    if (item.taxcheck === true) return true;
    // taxcheck: false means tax is NOT included, need to add tax
    if (item.taxcheck === false) return false;

    if (item.taxamounts && item.taxamounts.length > 0) {
      return item.taxamounts.some(tax => tax.TaxCheck === "Included");
    }
    return true;
  };

  // Helper function to get tax rate from item
  const getTaxes = () => {
    if (!businessTax) return [];

    const taxes = [];

    // GST
    if (businessTax?.dgsttax?.[0] === 1) {
      taxes.push({
        name: "GST",
        percentage: 18,
        rate: 0.18
      });
    }

    // Other taxes (CGST etc.)
    businessTax?.ugsttax?.forEach(t => {
      if (t.check) {
        taxes.push({
          name: t.title?.toUpperCase(), // CGST
          percentage: parseFloat(t.label),
          rate: parseFloat(t.label) / 100
        });
      }
    });

    return taxes;
  };



  useEffect(() => {
    if (selectedItem && businessTax) {
      const calculated = calculateTotal(selectedItem, totalPaidTillNow);
      setCalculation(calculated);
    }
  }, [businessTax]);


  const getAppliedTaxAmounts = (selectedItem, calculation) => {
    // Get taxes from business settings
    const taxes = getTaxes();

    // Create tax amounts array similar to what backend expects
    const taxAmounts = [];

    if (calculation.taxBreakdown && calculation.taxBreakdown.length > 0) {
      // Use the calculated tax breakdown
      calculation.taxBreakdown.forEach(tax => {
        taxAmounts.push({
          title: tax.name,
          percentage: `${tax.percentage}%`,
          amount: tax.amount,
          TaxCheck: calculation.isTaxIncluded ? "Included" : "Excluded"
        });
      });
    } else {
      // Fallback: create from business tax settings
      taxes.forEach(tax => {
        const taxAmount = calculation.taxableAmount * tax.rate;
        taxAmounts.push({
          title: tax.name,
          percentage: `${tax.percentage}%`,
          amount: Math.round(taxAmount),
          TaxCheck: calculation.isTaxIncluded ? "Included" : "Excluded"
        });
      });
    }

    // Add the TaxCheck flag at the end (as seen in your logs)
    taxAmounts.push({
      TaxCheck: calculation.isTaxIncluded ? "Included" : "Excluded"
    });

    return taxAmounts;
  };




  // Helper function to calculate individual member's contribution in group transaction
  const getIndividualMemberContribution = useCallback((transaction) => {
    console.log("transaction", transaction);

    if (!transaction) {
      return {
        individualPaid: 0,
        individualBasePrice: 0,
        individualTaxAmount: 0,
        individualTotalPaid: 0,
        hasDiscount: false,
        discountPercentage: 0,
        discountAmount: 0,
        memberCount: 1,
        isGroupTransaction: false,
        totalGroupBasePrice: 0,
        totalGroupTaxAmount: 0,
        totalGroupPaid: 0,
        isTaxIncluded: false,
        currentPlanId: null,
        taxRate: 0.18,
        planprice: 0
      };
    }

    const quantity = transaction.quantity || 1;
    const isGroupTransaction = quantity > 1;

    // IMPORTANT: itemprice is the PER-MEMBER base price
    const perMemberBasePrice = transaction.payment?.itemprice || 0;
    const planprice = transaction.payment?.itemprice || 0;

    const discount = transaction.payment?.discount;
    const taxAmounts = transaction.payment?.taxamounts || [];

    // Calculate total group base price
    const totalGroupBasePrice = perMemberBasePrice * quantity;

    // Get total paid amount from transaction
    const totalGroupPaid = transaction.payment?.totalamount || transaction.payment?.paid || 0;

    // Determine if tax is included in the price
    const isTaxIncludedInCurrent = taxAmounts.length > 0
      ? taxAmounts.some(tax => tax.TaxCheck === "Included")
      : false;

    // Get tax rate from taxamounts — sum ALL taxes (e.g. GST 18% + CGST 10% = 28%)
    let taxRate = 0.18;
    if (taxAmounts.length > 0) {
      const validTaxes = taxAmounts.filter(t => t.percentage);
      if (validTaxes.length > 0) {
        taxRate = validTaxes.reduce((sum, t) => sum + parseFloat(t.percentage) / 100, 0);
      }
    }

    const hasDiscount = discount && discount.amount > 0 && discount.EnterValue > 0;

    let individualBasePrice = perMemberBasePrice;
    let individualTaxAmount = 0;
    let individualTotalPaid = 0;
    let discountAmount = 0;
    let discountPercentage = 0;
    let totalGroupDiscountAmount = 0;

    if (hasDiscount) {
      discountPercentage = discount.EnterValue;

      if (isTaxIncludedInCurrent) {
        // TAX INCLUDED: Price includes tax, discount on base price only
        const perMemberBaseWithoutTax = perMemberBasePrice / (1 + taxRate);
        const totalGroupBaseWithoutTax = perMemberBaseWithoutTax * quantity;
        totalGroupDiscountAmount = totalGroupBaseWithoutTax * (discountPercentage / 100);
        const totalGroupBaseAfterDiscount = totalGroupBaseWithoutTax - totalGroupDiscountAmount;
        const totalGroupTaxAmount = totalGroupBaseAfterDiscount * taxRate;
        const totalGroupAfterDiscount = totalGroupBaseAfterDiscount + totalGroupTaxAmount;

        individualBasePrice = Math.round(perMemberBaseWithoutTax);
        individualTaxAmount = Math.round((totalGroupTaxAmount / quantity));
        individualTotalPaid = Math.round(totalGroupAfterDiscount / quantity);
        discountAmount = Math.round(totalGroupDiscountAmount / quantity);
      } else {
        // TAX EXCLUDED: Price doesn't include tax (YOUR CASE)
        // Calculate discount on base price
        totalGroupDiscountAmount = totalGroupBasePrice * (discountPercentage / 100);
        const totalGroupBaseAfterDiscount = totalGroupBasePrice - totalGroupDiscountAmount;
        const totalGroupTaxAmount = totalGroupBaseAfterDiscount * taxRate;
        const totalGroupAfterDiscount = totalGroupBaseAfterDiscount + totalGroupTaxAmount;

        individualBasePrice = perMemberBasePrice;
        individualTaxAmount = Math.round((totalGroupTaxAmount / quantity));
        individualTotalPaid = Math.round(totalGroupAfterDiscount / quantity);
        discountAmount = Math.round(totalGroupDiscountAmount / quantity);
      }
    } else {
      // NO DISCOUNT
      if (isTaxIncludedInCurrent) {
        // TAX INCLUDED
        const perMemberBaseWithoutTax = perMemberBasePrice / (1 + taxRate);
        const totalGroupTaxAmount = (perMemberBaseWithoutTax * quantity) * taxRate;

        individualBasePrice = Math.round(perMemberBaseWithoutTax);
        individualTaxAmount = Math.round(totalGroupTaxAmount / quantity);
        individualTotalPaid = perMemberBasePrice;
      } else {
        // TAX EXCLUDED (YOUR CASE)
        const totalGroupTaxAmount = totalGroupBasePrice * taxRate;

        individualBasePrice = perMemberBasePrice;
        individualTaxAmount = Math.round((totalGroupTaxAmount / quantity));
        individualTotalPaid = Math.round(perMemberBasePrice + (perMemberBasePrice * taxRate));
      }
    }

    // console.log("Calculation details:", {
    //   quantity,
    //   perMemberBasePrice,
    //   totalGroupBasePrice,
    //   totalGroupPaid,
    //   isTaxIncludedInCurrent,
    //   taxRate,
    //   individualBasePrice,
    //   individualTaxAmount,
    //   individualTotalPaid,
    //   hasDiscount,
    //   discountPercentage,
    //   discountAmount,
    //   isGroupTransaction
    // });






    // Build per-tax breakdown from stored taxAmounts
    const validTaxes = taxAmounts.filter(t => t.title);
    let taxBreakdown = [];
    if (validTaxes.length > 0 && individualTaxAmount > 0) {
      const totalRate = validTaxes.reduce((sum, t) => sum + parseFloat(t.percentage), 0);
      taxBreakdown = validTaxes.map(t => {
        const pct = parseFloat(t.percentage);
        return {
          name: t.title,
          percentage: pct,
          amount: totalRate > 0 ? Math.round(individualTaxAmount * (pct / totalRate)) : 0
        };
      });
    }

    return {
      individualBasePrice: individualBasePrice,
      individualTaxAmount: individualTaxAmount,
      individualTotalPaid: individualTotalPaid,
      hasDiscount: hasDiscount,
      discountPercentage: discountPercentage,
      discountAmount: discountAmount,
      memberCount: quantity,
      isGroupTransaction: isGroupTransaction,
      totalGroupBasePrice: totalGroupBasePrice,
      totalGroupTaxAmount: individualTaxAmount * quantity,
      totalGroupPaid: totalGroupPaid,
      isTaxIncluded: isTaxIncludedInCurrent,
      currentPlanId: transaction.itemid,
      taxRate: taxRate,
      planprice: planprice,
      taxBreakdown: taxBreakdown
    };
  }, []);

  // Get individual member's contribution
  const individualContribution = getIndividualMemberContribution(currentTransaction);

  // console.log("individualContribution",individualContribution)


  // Use individual amounts instead of group totals
  // const totalPaidTillNow = individualContribution.individualPaid;
  // const oldItemPrice = individualContribution.individualItemPrice;
  const oldItemName = currentTransaction?.name || (changeType === "addon" ? "Current Addon" : "Current Plan");
  // const currentPlanId = individualContribution.currentPlanId;



  const totalPaidTillNow = individualContribution.individualTotalPaid || 0;
  // const oldItemPrice = individualContribution.individualBasePrice || 0;
  const currentPlanId = individualContribution.currentPlanId;
  const oldItemPrice = currentTransaction?.payment?.itemprice || 0;


  // Helper function to get item price
  const getItemPrice = (item) => {
    if (!item) return 0;
    if (item.price !== undefined && item.price !== null) return item.price;
    if (item.props && Array.isArray(item.props) && item.props.length > 0) {
      const priceProp = item.props.find(prop => prop.propvalue !== undefined);
      if (priceProp && priceProp.propvalue) return parseFloat(priceProp.propvalue) || 0;
    }
    return 0;
  };

  // Helper function to get item display name
  const getItemDisplayName = (item) => {
    if (!item) return "";
    if (changeType === "addon") return item.name || item.title || "Addon";
    if (item.baseplan) {
      const basePlanName = BasePlanName(plans, item.baseplan);
      return basePlanName ? `${basePlanName} - ${item.name}` : item.name;
    }
    return item.name;
  };

  // Calculate total based on selected plan's tax configuration
  // const calculateTotal = useCallback((newItem, paidAmount) => {
  //   const newItemPrice = getItemPrice(newItem);
  //   const isTaxIncludedInNew = isTaxIncluded(newItem);
  //   const taxRate = getTaxRate(newItem);

  //   let remainingAmount = newItemPrice - paidAmount;
  //   const isRefund = remainingAmount < 0;
  //   const refundAmount = isRefund ? Math.abs(remainingAmount) : 0;

  //   let finalPayable = 0;
  //   let taxableAmount = 0;
  //   let taxAmount = 0;

  //   if (!isRefund && remainingAmount > 0) {
  //     if (isTaxIncludedInNew) {
  //       // Tax is already included in the price
  //       finalPayable = remainingAmount;
  //       // Calculate tax breakdown for display
  //       const basePrice = remainingAmount / (1 + taxRate);
  //       taxableAmount = basePrice;
  //       taxAmount = remainingAmount - basePrice;
  //     } else {
  //       // Tax needs to be added (taxcheck: false)
  //       taxableAmount = remainingAmount;
  //       taxAmount = remainingAmount * taxRate;
  //       finalPayable = remainingAmount + taxAmount;
  //     }
  //   }

  //   return {
  //     newItemPrice,
  //     alreadyPaid: paidAmount,
  //     remainingAmount: Math.abs(remainingAmount),
  //     taxableAmount: Math.round(taxableAmount),
  //     taxAmount: Math.round(taxAmount),
  //     finalPayable: Math.round(finalPayable),
  //     isRefund,
  //     refundAmount: Math.round(refundAmount),
  //     isTaxIncluded: isTaxIncludedInNew,
  //     taxRate: taxRate
  //   };
  // }, []);




  const calculateTotal = useCallback((newItem, paidAmount) => {
    const newItemPrice = getItemPrice(newItem);
    const isTaxIncludedInNew = isTaxIncluded(newItem);
    const taxes = getTaxes(newItem);

    let taxableAmount = 0;
    let totalTaxAmount = 0;
    let taxBreakdown = [];
    let grandTotal = 0;

    if (isTaxIncludedInNew) {
      grandTotal = newItemPrice;

      const totalRate = taxes.reduce((sum, t) => sum + t.rate, 0);
      const base = newItemPrice / (1 + totalRate);

      taxableAmount = base;

      taxBreakdown = taxes.map(t => {
        const amt = base * t.rate;
        totalTaxAmount += amt;
        return { ...t, amount: Math.round(amt) };
      });

    } else {
      taxableAmount = newItemPrice;

      taxBreakdown = taxes.map(t => {
        const amt = newItemPrice * t.rate;
        totalTaxAmount += amt;
        return { ...t, amount: Math.round(amt) };
      });

      grandTotal = newItemPrice + totalTaxAmount;
    }

    let remainingAmount = grandTotal - paidAmount;

    return {
      newItemPrice,
      alreadyPaid: paidAmount,

      taxableAmount: Math.round(taxableAmount),
      taxAmount: Math.round(totalTaxAmount),
      taxBreakdown, // IMPORTANT

      grandTotal: Math.round(grandTotal),

      finalPayable: remainingAmount < 0 ? 0 : Math.round(remainingAmount),
      remainingAmount: Math.abs(remainingAmount),

      isRefund: remainingAmount < 0,
      refundAmount: remainingAmount < 0 ? Math.abs(remainingAmount) : 0,

      isTaxIncluded: isTaxIncludedInNew
    };
  }, []);


  // Handle item selection
  const handleItemSelect = (item) => {


    //     console.log("22222SELECTED ITEM FULL:", item);
    // console.log("22222222TAX DATA:", item.taxamounts);



    setSelectedItem(item);
    const calculated = calculateTotal(item, totalPaidTillNow);
    setCalculation(calculated);
  };

  // Filter items based on search
  const filterItems = (itemsList) => {
    if (!searchTerm) return itemsList;
    const searchLower = searchTerm.toLowerCase();
    return itemsList.filter(item => {
      const displayName = getItemDisplayName(item);
      return displayName.toLowerCase().includes(searchLower) ||
        (item.name && item.name.toLowerCase().includes(searchLower));
    });
  };

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedItem(null);
      setSearchTerm("");
      setError("");
      setShowPayment(false);
      setCalculation({
        newItemPrice: 0,
        alreadyPaid: 0,
        remainingAmount: 0,
        taxableAmount: 0,
        taxAmount: 0,
        finalPayable: 0,
        isRefund: false,
        refundAmount: 0
      });
    }
  }, [open]);

  // Handle plan/addon change with payment
  const handleChangeWithPayment = async (paymentData = null) => {
    if (!selectedItem) {
      setError(`Please select a ${changeType === "addon" ? "addon" : "plan"}`);
      return;
    }

    setLoading(true);
    setError("");

    const loggedUser = JSON.parse(localStorage.getItem("loggedUserInfo"));
    const gymid = loggedUser?.gymid;

    try {
      const selectedItemPrice = getItemPrice(selectedItem);

      let paymentInfoForBackend = null;

      if (paymentData && paymentData.paidAmount > 0) {
        const payinfoObject = {
          mode: paymentData.mode,
          balmode: 1,
          info: {
            cash_amt: paymentData.paymentInfo?.cash_amt || "",
            cc_amt: paymentData.paymentInfo?.cc_amt || "",
            upi_amt: paymentData.paymentInfo?.upi_amt || "",
            upi_input: paymentData.paymentInfo?.upi_input || "",
            wallet_amt: paymentData.paymentInfo?.wallet_amt || "",
            wallet_input: paymentData.paymentInfo?.wallet_input || "",
            userpaymethod: paymentData.paymentInfo?.userpaymethod || "",
            ...(paymentData.mode === 5 && {
              coupon_input: paymentData.paymentInfo?.coupon_input || "",
              coupon_amt: paymentData.paymentInfo?.coupon_amt || ""
            })
          },
          balance: paymentData.balance || 0
        };

        paymentInfoForBackend = {
          mode: paymentData.mode,
          balmode: 1,
          info: payinfoObject.info,
          balance: paymentData.balance || 0,
          payinfoString: JSON.stringify(payinfoObject),
          transid: paymentData.transId,
          amount: paymentData.paidAmount,
          paidAmount: paymentData.paidAmount
        };
      }


      // Get the applied tax amounts based on calculation
      const appliedTaxAmounts = getAppliedTaxAmounts(selectedItem, calculation);

      // console.log("Applied Tax Amounts to send:", appliedTaxAmounts);


      const fullPlanName = selectedItem.parentPlanName
        ? `${selectedItem.parentPlanName}-${selectedItem.name}`
        : selectedItem.name;

      const payload = {
        transactionId: currentTransaction._id,
        changeType: changeType,

        item: {
          _id: selectedItem._id,
          name: selectedItem.name,
          price: getItemPrice(selectedItem),
          taxcheck: selectedItem.taxcheck,
          taxamounts: appliedTaxAmounts,
          baseplanid: selectedItem?.baseplan || undefined,
          validityflag: selectedItem.validityflag,
          numberofdays: selectedItem.numberofdays,
          validtimetype: selectedItem.validtimetype,
          rentbasis: selectedItem.rentbasis,
          freeguestpasses: selectedItem.freeguestpasses || 0,
          sessionvalue: selectedItem.sessionvalue || 0,
          sessionsharinglimit: selectedItem.sessionsharinglimit || 0,
          fullname: fullPlanName || undefined,
        },

        // payment (only if exists)
        ...(paymentInfoForBackend && { paymentInfo: paymentInfoForBackend }),

        hasDiscount: individualContribution.hasDiscount,
        discountPercentage: individualContribution.discountPercentage,
        // needed for calculation logic
        oldItemTotalPaid: totalPaidTillNow
      };


      if (paymentInfoForBackend) {
        payload.paymentInfo = paymentInfoForBackend;
        payload.payinfo = {
          mode: paymentData.mode,
          balmode: 1,
          info: paymentInfoForBackend.info,
          balance: paymentData.balance || 0
        };
      }

      const response = await updateMemberPlan(gymid, payload);

      if (response && response.ok) {
        const result = await response.json();

        if (result.success) {
          setOpen(false);
          setShowPayment(false);

          let successMsg = "";
          if (calculation.finalPayable > 0) {
            successMsg = `Payment of ₹${calculation.finalPayable} completed successfully! ${changeType === "addon" ? "Addon" : "Plan"} updated.`;
          } else if (calculation.isRefund) {
            successMsg = `Refund of ₹${calculation.refundAmount} will be credited to wallet. ${changeType === "addon" ? "Addon" : "Plan"} updated.`;
          } else {
            successMsg = `${changeType === "addon" ? "Addon" : "Plan"} changed successfully!`;
          }

          if (individualContribution.isGroupTransaction) {
            successMsg += ` (Note: This was a group transaction with ${individualContribution.memberCount} members. Only this member's share was processed.)`;
          }

          onSuccess({
            ...result.data,
            showReceipt: true,
            isPlanChange: changeType === "plan",
            isAddonChange: changeType === "addon",
            paymentCompleted: calculation.finalPayable > 0,
            amountPaid: calculation.finalPayable,
            isRefund: calculation.isRefund,
            refundAmount: calculation.refundAmount,
            isGroupTransaction: individualContribution.isGroupTransaction,
            memberCount: individualContribution.memberCount
          });
        } else {
          setError(result.message || `Failed to update ${changeType}`);
        }
      } else {
        const errorData = await response?.json();
        setError(errorData?.message || `Failed to update ${changeType}. Please try again.`);
      }
    } catch (err) {
      console.error(`Error updating ${changeType}:`, err);
      setError(err.message || `An error occurred while updating the ${changeType}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle confirm button click
  const handleConfirmClick = () => {
    if (!selectedItem) {
      setError(`Please select a ${changeType === "addon" ? "addon" : "plan"}`);
      return;
    }

    if (calculation.finalPayable > 0) {
      setShowPayment(true);
    } else {
      handleChangeWithPayment();
    }
  };


  // Get all items (plans or addons) with filtering
  // const getAllItems = () => {
  //   if (changeType === "addon") {
  //     // For addons, filter based on price > current addon's price
  //     return (addons || [])
  //       .map(addon => ({
  //         ...addon,
  //         price: getItemPrice(addon)
  //       }))
  //       .filter(addon => {
  //         const addonPrice = getItemPrice(addon);
  //         // Only show addons with price > current addon price
  //         if (addonPrice <= oldItemPrice) return false;

  //         // Don't show the current addon if it's the same
  //         if (currentPlanId && addon._id === currentPlanId) return false;

  //         return true;
  //       });
  //   }

  //   // For plans
  //   const allPlanItems = [];
  //   const seenIds = new Set();

  //   (plans || []).forEach(plan => {
  //     if (seenIds.has(plan._id)) return;

  //     if (plan.subplans && Array.isArray(plan.subplans) && plan.subplans.length > 0) {
  //       plan.subplans.forEach(subplan => {
  //         if (!seenIds.has(subplan._id)) {
  //           const subplanPrice = subplan.price || 0;
  //           // Only show subplans with price > current plan price
  //           if (subplanPrice > oldItemPrice && currentPlanId !== subplan._id) {
  //             allPlanItems.push({
  //               ...subplan,
  //               parentPlanName: plan.name,
  //               isSubplan: true,
  //               price: subplanPrice
  //             });
  //           }
  //           seenIds.add(subplan._id);
  //         }
  //       });
  //     } else if (!plan.baseplan) {
  //       const planPrice = plan.price || 0;
  //       // Only show plans with price > current plan price
  //       if (planPrice > oldItemPrice && currentPlanId !== plan._id) {
  //         allPlanItems.push({
  //           ...plan,
  //           parentPlanName: null,
  //           isSubplan: false,
  //           price: planPrice
  //         });
  //       }
  //       seenIds.add(plan._id);
  //     }
  //   });

  //   return allPlanItems;
  // };






  const getAllItems = () => {
  if (changeType === "addon") {
    return (addons || [])
      .map(addon => ({
        ...addon,
        price: getItemPrice(addon)
      }))
      .filter(addon => {
        const addonPrice = getItemPrice(addon);
        
        // Calculate final price including excluded tax
        const addonCalculation = calculateTotal(addon, 0);
        const finalAddonPrice = addonCalculation.grandTotal;

        // Compare final price (with tax) against totalPaidTillNow
        if (finalAddonPrice <= totalPaidTillNow) return false;
        if (currentPlanId && addon._id === currentPlanId) return false;

        return true;
      });
  }

  // For plans
  const allPlanItems = [];
  const seenIds = new Set();

  (plans || []).forEach(plan => {
    if (seenIds.has(plan._id)) return;

    if (plan.subplans && Array.isArray(plan.subplans) && plan.subplans.length > 0) {
      plan.subplans.forEach(subplan => {
        if (!seenIds.has(subplan._id)) {
          const subplanPrice = subplan.price || 0;

          // Calculate final price including excluded tax
          const subplanCalculation = calculateTotal(subplan, 0);
          const finalSubplanPrice = subplanCalculation.grandTotal;

          if (finalSubplanPrice > totalPaidTillNow && currentPlanId !== subplan._id) {
            allPlanItems.push({
              ...subplan,
              parentPlanName: plan.name,
              isSubplan: true,
              price: subplanPrice
            });
          }
          seenIds.add(subplan._id);
        }
      });
    } else if (!plan.baseplan) {
      const planPrice = plan.price || 0;

      // Calculate final price including excluded tax
      const planCalculation = calculateTotal(plan, 0);
      const finalPlanPrice = planCalculation.grandTotal;

      if (finalPlanPrice > totalPaidTillNow && currentPlanId !== plan._id) {
        allPlanItems.push({
          ...plan,
          parentPlanName: null,
          isSubplan: false,
          price: planPrice
        });
      }
      seenIds.add(plan._id);
    }
  });

  return allPlanItems;
};

  // Render items
  const renderItems = () => {
    const allItems = getAllItems();
    const filteredItems = filterItems(allItems);

    const sortedItems = [...filteredItems].sort((a, b) => {
      const priceA = getItemPrice(a);
      const priceB = getItemPrice(b);
      return priceA - priceB; // Sort by price ascending
    });

    if (sortedItems.length === 0) {
      return (
        <div className="text-center p-5">
          <MDBIcon fas icon={changeType === "addon" ? "puzzle-piece" : "id-card"} size="3x" className="text-muted mb-3" />
          <p className="mb-2">No upgrade plans available</p>
          <p className="text-muted">
            You have already paid ₹{totalPaidTillNow.toLocaleString()}.<br />
            Only plans with price greater than this amount are shown.
          </p>
        </div>
      );
    }

    return (
      <div className="items-container">
        <div className="mb-3 text-muted small">
          {/* <MDBIcon fas icon="info-circle" className="me-1" /> */}
        </div>
        <MDBRow className="g-2">
          {sortedItems.map((item) => {
            const isSelected = selectedItem?._id === item._id;
            const displayName = getItemDisplayName(item);
            const itemPrice = getItemPrice(item);
            const taxIncluded = isTaxIncluded(item);
            const priceDifference = itemPrice - totalPaidTillNow;

            return (
              <MDBCol xs="6" sm="3" md="4" key={item._id}>
                <MDBCard
                  className={`subplan-card h-100 upgrade-plan ${isSelected ? 'upgrade-plan-selected' : ''}`}
                  style={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #3b71ca' : '1px solid #e0e0e0',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleItemSelect(item)}
                >
                  <MDBCardBody className="p-3 text-center">
                    <div className="fw-semibold mb-1" style={{ fontSize: '0.85rem', color: '#555' }}>
                      {displayName}
                    </div>
                    <div className="text-primary fw-bold mb-2" style={{ fontSize: '1.2rem' }}>
                      ₹{itemPrice.toLocaleString()}
                    </div>
                    <span className={`plan-tax-badge ${taxIncluded ? 'tax-badge-included' : 'tax-badge-excluded'}`}>
                      <span className="plan-tax-dot"></span>
                      {taxIncluded ? 'Tax included' : 'Tax excluded'}
                    </span>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            );
          })}
        </MDBRow>
      </div>
    );
  };

  const renderBillDetails = () => {
    return (
      <div className="quote-summary bg-light rounded p-4 sticky-top" style={{ top: '20px' }}>
        <h5 className="mb-2 text-left">
          Billing Details
        </h5>

        {/* Group Transaction Warning */}
        {individualContribution.isGroupTransaction && (
          <MDBAlert color="info" className="mb-3 py-2">
            <small>
              This is a group transaction with <strong>{individualContribution.memberCount} members</strong>.<br />
              Showing <strong>individual member's share</strong>.
            </small>
          </MDBAlert>
        )}
        <div className="position-relative">
          {/* Current Item Section */}
          <div className="current-item p-3 current-plancard">
            <div className="d-block mb-1">
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#e07a00', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Current {changeType === "addon" ? "Addon" : "Plan"}
              </span>
              <p className="fw-bold mb-0">{oldItemName || "—"}</p>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-1">
              <span className="text-muted">Plan Price </span>
              <span>₹{individualContribution.individualBasePrice?.toLocaleString() || 0}</span>
            </div>

            {individualContribution.individualTaxAmount > 0 && (
              individualContribution.taxBreakdown?.length > 0 ? (
                individualContribution.taxBreakdown.map((tax, i) => (
                  tax.amount > 0 && (
                    <div key={i} className="d-flex justify-content-between align-items-center mt-1">
                      <span className="text-muted">+ {tax.name} ({tax.percentage}%)</span>
                      <span>₹{tax.amount.toLocaleString()}</span>
                    </div>
                  )
                ))
              ) : (
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <span className="text-muted">+ GST ({Math.round(individualContribution.taxRate * 100)}%)</span>
                  <span>₹{individualContribution.individualTaxAmount?.toLocaleString() || 0}</span>
                </div>
              )
            )}

            {/* <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
          <span className="fw-bold">Total Price (Including Tax)</span>
          <span className="fw-bold text-primary">₹{individualContribution.individualTotalPaid?.toLocaleString() || 0}</span>
        </div>
         */}
            <div className="d-flex justify-content-between align-items-center text-success mt-1">
              <span>
                Paid Amount
              </span>
              <span className="fw-bold"> <MDBIcon fas icon="check-circle" className="me-1" /> ₹{individualContribution.individualTotalPaid?.toLocaleString() || 0}</span>
            </div>
          </div>

          {selectedItem && (
            <div className="transform-line">
              <i className="fa-solid fa-caret-down"></i>
            </div>
          )}


          {/* Selected Item Section */}
          <div className="selected-item mb-4 selected-plan p-3">
            {/* <div className="text-muted mb-2">
          <MDBIcon fas icon="arrow-right" className="me-1" />
          New Selection
        </div> */}
            {selectedItem ? (
              <>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#3b71ca', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Upgrade {changeType === "addon" ? "Addon" : "Plan"}
                  </span>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="fw-bold">
                      {getItemDisplayName(selectedItem)}
                    </span>
                    <span className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                      ₹{getItemPrice(selectedItem).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center rounded">
                Choose a plan to continue with your upgrade
              </div>
            )}
          </div>
        </div>
        {/* Payment Breakdown */}
        <div className="payment-breakdown">
          <div className="text-dark fw-bold mb-2">
            Payment Breakdown
          </div>

          <div className="d-flex justify-content-between mb-1">
            <span>New {changeType === "addon" ? "Addon" : "Plan"} Price</span>
            {/* <span>₹{calculation.newItemPrice?.toLocaleString() || 0}</span> */}

              <span>₹{calculation.taxableAmount?.toLocaleString() || 0}</span>

          </div>


          {/* Tax - Only show if tax is excluded and not refund */}
          {/* {!calculation.isRefund && calculation.remainingAmount > 0 && !calculation.isTaxIncluded && (
          <div className="d-flex justify-content-between mb-2">
            <span>Tax ({Math.round(calculation.taxRate * 100)}% GST)</span>
            <span>+ ₹{calculation.taxAmount?.toLocaleString() || 0}</span>
          </div>
        )} */}

          {calculation.taxBreakdown?.map((tax, i) => (
            <div key={i} className="d-flex justify-content-between mb-1">
              <span>{tax.name} ({tax.percentage}%)</span>
              <span>+ ₹{tax.amount.toLocaleString()}</span>
            </div>
          ))}

          <div className="d-flex justify-content-between mb-1">
            <span>Upgrading Plan Price</span>
            <span>₹{(calculation.grandTotal || 0).toLocaleString()}</span>
          </div>

          <div className="d-flex justify-content-between mb-1">
            <span>Paid Amount</span>
            <span className="text-danger">- ₹{calculation.alreadyPaid?.toLocaleString() || 0}</span>
          </div>

          <hr className="my-2" />

          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-bold">Balance to Pay </span>
            <span className="fw-bold text-primary">
              ₹{calculation.finalPayable?.toLocaleString() || 0}
            </span>
          </div>

          {/* Refund info */}
          {calculation.isRefund && (
            <MDBAlert color="info" className="mt-3 mb-0">
              <MDBIcon fas icon="info-circle" className="me-2" />
              ₹{calculation.refundAmount.toLocaleString()} will be credited to wallet
            </MDBAlert>
          )}

          {/* Zero payment info */}
          {selectedItem && calculation.finalPayable === 0 && !calculation.isRefund && (
            <MDBAlert color="success" className="mt-3 mb-0">
              <MDBIcon fas icon="check-circle" className="me-2" />
              No payment required! {changeType === "addon" ? "Addon" : "Plan"} change successful.
            </MDBAlert>
          )}
        </div>
      </div>
    );
  };

  const titleText = changeType === "addon" ? "Change Addon" : "Upgrade Membership Plan";
  const iconType = changeType === "addon" ? "puzzle-piece" : "arrow-up";

  return (
    <>
      <MDBModal open={open} setOpen={setOpen} tabIndex='-1' size="xl" className="change-plan-modal">
        <MDBModalDialog size="xl">
          <MDBModalContent>
            <MDBModalHeader className="bg-light">
              <MDBModalTitle className="h4">
                <MDBIcon fas icon={iconType} className="me-2" />
                {titleText}
              </MDBModalTitle>


              <MDBBtn className='btn-close' color='none' onClick={() => setOpen(false)}   disabled={loading} ></MDBBtn>
            </MDBModalHeader>

            <MDBModalBody className="p-4">
              {error && error.includes("successfully") ? (
                <MDBAlert color="success" className="mb-3" onClose={() => setError("")} dismiss>
                  <MDBIcon fas icon="check-circle" className="me-2" />
                  {error}
                </MDBAlert>
              ) : error && (
                <MDBAlert color="danger" className="mb-3" onClose={() => setError("")} dismiss>
                  <MDBIcon fas icon="exclamation-circle" className="me-2" />
                  {error}
                </MDBAlert>
              )}

              <MDBRow>
                <MDBCol md="7" className="border-end">
                  <div className="mb-4">
                    <MDBInput
                      label={`Search ${changeType === "addon" ? "Addons" : "Plans"}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  {renderItems()}
                </MDBCol>

                <MDBCol md="5">
                  {renderBillDetails()}
                </MDBCol>
              </MDBRow>
            </MDBModalBody>

            <MDBModalFooter className="bg-light">
              <MDBBtn
                color='secondary'
                onClick={() => !loading && setOpen(false)}
                disabled={loading}
                style={{
                  pointerEvents: loading ? 'none' : 'auto',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Cancel
              </MDBBtn>
              <MDBBtn
                color='primary'
                onClick={handleConfirmClick}
                disabled={!selectedItem || loading}
                size="lg"
              >
                {loading ? (
                  <>
                    <MDBSpinner size='sm' className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                     Upgrade
                  </>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Payment Popup Modal */}
      <MDBModal open={showPayment} setOpen={setShowPayment} tabIndex='-1' size="lg" centered>
        <MDBModalDialog size="lg">
          <MDBModalContent>
            <MDBModalHeader className="bg-light">
              <MDBModalTitle className="h5">
                <MDBIcon fas icon="credit-card" className="me-2 text-primary" />
                Complete Payment
              </MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={() => setShowPayment(false)}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody className="p-4">
              <PlanChangePayment
                open={showPayment}
                onClose={() => setShowPayment(false)}
                amountToPay={calculation.finalPayable}
                currentPlanName={oldItemName}
                selectedPlanName={selectedItem ? getItemDisplayName(selectedItem) : ""}
                onPaymentComplete={(paymentData) => {
                  setShowPayment(false);
                  handleChangeWithPayment(paymentData);
                }}
                saving={loading}
                serverError={error}
              />
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
};

export default ChangePlanModal;